import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Types
import { getUserPermissions, Roles } from '@repo/frontend-types';

// Data

// Context
import { useUserStore } from '@repo/front-end-state';

// Utils
import { extractClaims } from '@repo/front-end-utils';
import { clearOrganizationData } from '@repo/front-end-utils';

// Components
import Config from '../../../common/Config';
import { removeProtocol } from '@repo/front-end-utils';
import { TLSpinnerPage } from '@repo/ui';
import { returnToFromState } from '../../../components/Auth0/returnTo';

import { getOrganisationById, syncUserOnLogin, getLegalStatus } from '@repo/front-end-services';
const LoginCallbackPage: React.FC = () => {
  const { user, isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();
  const { setAuthMetadata } = useUserStore();
  const navigate = useNavigate();
  // Where the person was going before sign-in (`returnTo.ts`), else the twin page.
  const returnTo = returnToFromState(useLocation().state);
  const destination = returnTo ?? '/twin';

  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);
      } catch (error) {
        console.warn('Silent auth failed:', error);
        // Redirect to login if silent auth fails, keeping where the person was going.
        await loginWithRedirect(returnTo ? { appState: { returnTo } } : undefined);
      }
    };

    if (isAuthenticated) {
      getAccessToken();
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, returnTo]);

  // Best-effort: get-or-create this user's app_user row at login (advances last_seen_at).
  // Fire-and-forget — never blocks login or navigation (FR4); failures are swallowed.
  useEffect(() => {
    if (accessToken) {
      void syncUserOnLogin(Config.middlewareApi, accessToken);
    }
  }, [accessToken]);

  const setOrganisation = useCallback(
    async (organisationName: string, bID: string) => {
      if (accessToken) {
        const organisation = await getOrganisationById(Config.middlewareApi, accessToken, bID);

        if (organisation) {
          setAuthMetadata({
            orgID: organisation.id,
            orgBID: bID,
            // Mirror the geographic coordinates onto authMetadata so
            // flyToTarget(orgBID) can resolve the org HQ pose (CP4 path).
            // Without these, Home / org-fly throws IncompleteTargetData.
            // OrganisationSelector and useSpotlight both also populate these
            // — this is the login-callback path for users with a single
            // auto-assigned org.
            orgLatitude: organisation.latitude,
            orgLongitude: organisation.longitude,
            organisation: organisationName,
            dgraphUrl: removeProtocol(organisation.dgraph_url),
            dgraph_namespace: organisation.dgraph_namespace,
            envName: '',
            environment: '',
            name: user?.name,
            firstName: user?.given_name,
            lastName: user?.family_name,
            email: user?.email,
            tokens: {
              accessToken: accessToken,
            },
            tsdbUrl: organisation.tsdb_url,
          });
        }
      }
    },
    [accessToken, setAuthMetadata, user?.name, user?.given_name, user?.family_name, user?.email],
  );

  useEffect(() => {
    const handleRedirect = async () => {
      if (isLoading) return; // Wait for loading to complete

      // Clear any stale organization data from previous sessions before processing new user
      // This ensures a clean state for the new login
      clearOrganizationData();

      if (isAuthenticated && user && accessToken) {
        let redirectPath = '/something-went-wrong';
        let redirectState = { state: {}, replace: true };
        let claims = extractClaims(user, Config.auth0Namespace);

        // Ensure a user cannot proceed if they haven't been assigned a role
        if (claims.roles && claims.roles.length !== 0) {
          // Set what data we do have in AuthMetaData
          setAuthMetadata({
            personalisation: claims.personalisation?.camera_start_args
              ? {
                  camera_start_args: {
                    lat: parseFloat(String(claims.personalisation.camera_start_args.lat)),
                    lng: parseFloat(String(claims.personalisation.camera_start_args.lng)),
                    zoom: parseFloat(String(claims.personalisation.camera_start_args.zoom)),
                    bearing: parseFloat(String(claims.personalisation.camera_start_args.bearing)),
                    pitch: parseFloat(String(claims.personalisation.camera_start_args.pitch)),
                  },
                }
              : undefined,
            roles: claims.roles,
            permissions: getUserPermissions(claims.roles),
            tokens: {
              mapboxToken: claims.mapboxToken,
              modelUrlKey: claims.modelUrlKey,
            },
          });

          console.log(`claims.modelUrlKey`, claims.modelUrlKey);

          if (claims.organisations && claims.organisations.length > 0) {
            redirectPath = destination;
            if (
              claims.roles.includes(Roles.CUSTOMER_ADMIN) ||
              claims.roles.includes(Roles.CUSTOMER_USER)
            ) {
              const auth0OrgMetadata = claims.organisations[0].split(':');
              setOrganisation(auth0OrgMetadata[0], auth0OrgMetadata[1]);
            }
          }

          if (
            !(claims.roles.includes(Roles.TL_ADMIN) || claims.roles.includes(Roles.PARTNER_USER))
          ) {
            // Legal gate computed server-side (FR4). Fail-open (NF2): a status failure proceeds
            // to the app rather than blocking a legitimate login.
            try {
              const status = await getLegalStatus(Config.middlewareApi, accessToken);
              if (status.required.length > 0) {
                redirectPath = '/account/legal';
                redirectState = {
                  state: {
                    required: status.required,
                    organisationBid: status.organisationBid,
                    redirectPath: destination,
                  },
                  replace: true,
                };
              } else {
                redirectPath = destination;
              }
            } catch (error) {
              console.warn('Legal status check failed — proceeding as valid:', error);
              redirectPath = destination;
            }
          }
        }

        navigate(redirectPath, redirectState);
      }
    };

    handleRedirect();
  }, [
    accessToken,
    isAuthenticated,
    isLoading,
    user,
    navigate,
    setAuthMetadata,
    setOrganisation,
    destination,
  ]);

  if (isLoading) {
    return <TLSpinnerPage loadingMessages={[]} />;
  }
};

export default LoginCallbackPage;
